import React, { useEffect, useRef, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import { PhotoCamera as PhotoCameraIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Box, Button, CircularProgress, Menu, MenuItem, Stack, Typography } from '@/shared/ui';
import AuthAvatar from './AuthAvatar';
import { AvatarCropDialog } from './AvatarCropDialog';
import { verifyAvatarSrcLoads } from './prefetchAvatarSrc';
import { getApiErrorMessage } from '@/shared/utils/apiError';
import { toast } from '@/shared/feedback/toast';

export interface AvatarUploadControlProps {
  /** Current avatar API path, or null when none. */
  apiSrc?: string | null;
  /** Fallback letters / icon when no image. */
  fallback?: React.ReactNode;
  size?: number;
  disabled?: boolean;
  /** Return the new avatar API path when upload succeeds so display can be verified. */
  onUpload: (file: File) => Promise<string | null | void>;
  onRemove?: () => Promise<void>;
  /** i18n namespace for labels (identity | staff). */
  labelsNs?: 'identity' | 'staff' | 'internalChat';
  /**
   * `full` keeps title / hint / buttons (form pages).
   * `compact` is click-on-avatar only (staff detail header).
   */
  variant?: 'full' | 'compact';
  /** Skip the save toast — used when the crop is only staged locally. */
  notifyOnUpload?: boolean;
  /** Hide the heading; pair with a short format hint like the create-staff wizard. */
  hideTitle?: boolean;
}

const ACCEPT = 'image/jpeg,image/png,.jpg,.jpeg,.png';

/**
 * Staff / profile avatar editor: click the circle to pick a file, crop/zoom
 * before upload, optional side buttons. Used on Staff details/edit and Profile.
 */
const AvatarUploadControl: React.FC<AvatarUploadControlProps> = ({
  apiSrc,
  fallback = '?',
  size = 96,
  disabled = false,
  onUpload,
  onRemove,
  labelsNs = 'identity',
  variant = 'full',
  notifyOnUpload = true,
  hideTitle = false,
}) => {
  const { t } = useTranslation([labelsNs, 'common']);
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropFileName, setCropFileName] = useState('avatar.jpg');
  const [cropping, setCropping] = useState(false);

  const canEdit = !disabled && !busy && !cropping;
  const progressSize = Math.round(size * 0.45);

  useEffect(() => {
    return () => {
      if (cropSrc?.startsWith('blob:')) {
        URL.revokeObjectURL(cropSrc);
      }
    };
  }, [cropSrc]);

  const closeCrop = () => {
    setCropOpen(false);
    if (cropSrc?.startsWith('blob:')) {
      URL.revokeObjectURL(cropSrc);
    }
    setCropSrc(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const pickFile = (): void => {
    if (!canEdit) return;
    setMenuAnchor(null);
    inputRef.current?.click();
  };

  const handleFileSelected = (file: File | undefined): void => {
    if (!file) return;
    if (cropSrc?.startsWith('blob:')) {
      URL.revokeObjectURL(cropSrc);
    }
    setCropFileName(file.name || 'avatar.jpg');
    setCropSrc(URL.createObjectURL(file));
    setCropOpen(true);
  };

  const handleCropConfirm = async (file: File): Promise<void> => {
    setCropping(true);
    try {
      const uploadedSrc = await onUpload(file);
      if (notifyOnUpload) {
        const srcToVerify = typeof uploadedSrc === 'string' ? uploadedSrc : apiSrc;
        const displayOk = srcToVerify ? await verifyAvatarSrcLoads(srcToVerify) : true;
        if (displayOk) {
          toast.success(t('avatar.uploadSuccess', { ns: labelsNs }));
        } else {
          toast.warning(t('avatar.uploadDisplayFailed', { ns: labelsNs }));
        }
      }
      closeCrop();
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('avatar.uploadFailed', { ns: labelsNs })));
    } finally {
      setCropping(false);
    }
  };

  const handleRemove = async (): Promise<void> => {
    if (!onRemove || !canEdit) return;
    setMenuAnchor(null);
    setBusy(true);
    try {
      await onRemove();
      toast.success(t('avatar.removeSuccess', { ns: labelsNs }));
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('avatar.removeFailed', { ns: labelsNs })));
    } finally {
      setBusy(false);
    }
  };

  const openMenu = (event: MouseEvent<HTMLElement>) => {
    if (!canEdit || !apiSrc || !onRemove) return;
    event.preventDefault();
    setMenuAnchor(event.currentTarget);
  };

  const cropLabels = {
    title: t('avatar.cropTitle', { ns: labelsNs }),
    hint: t('avatar.cropHint', { ns: labelsNs }),
    zoom: t('avatar.cropZoom', { ns: labelsNs }),
    cancel: t('avatar.cropCancel', { ns: labelsNs }),
    confirm: t('avatar.cropConfirm', { ns: labelsNs }),
  };

  const avatarEditor = (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: '50%',
        '&:hover .avatar-edit-overlay': { opacity: canEdit ? 1 : 0 },
      }}
    >
      <AuthAvatar
        apiSrc={apiSrc}
        sx={{
          width: size,
          height: size,
          fontSize: size * 0.35,
          cursor: canEdit ? 'pointer' : 'default',
        }}
        onClick={canEdit ? pickFile : undefined}
        onContextMenu={openMenu}
        aria-label={canEdit ? t('avatar.change', { ns: labelsNs }) : undefined}
        role={canEdit ? 'button' : undefined}
        tabIndex={canEdit ? 0 : undefined}
        onKeyDown={
          canEdit
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  pickFile();
                }
              }
            : undefined
        }
      >
        {fallback}
      </AuthAvatar>
      {canEdit ? (
        <Box
          className="avatar-edit-overlay"
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.42)',
            color: 'common.white',
            opacity: 0,
            transition: 'opacity 0.15s ease',
            pointerEvents: 'none',
          }}
        >
          <PhotoCameraOutlinedIcon sx={{ fontSize: size * 0.42 }} />
        </Box>
      ) : null}
      {busy || cropping ? (
        <CircularProgress
          size={progressSize}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            mt: `${-Math.round(progressSize / 2)}px`,
            ml: `${-Math.round(progressSize / 2)}px`,
          }}
        />
      ) : null}
      {!disabled ? (
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          hidden
          onChange={(event) => handleFileSelected(event.target.files?.[0])}
        />
      ) : null}
    </Box>
  );

  return (
    <>
      {variant === 'compact' ? (
        avatarEditor
      ) : (
        <Stack direction="row" spacing={2} alignItems="center">
          {avatarEditor}
          <Box>
            {!hideTitle ? (
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {t('avatar.title', { ns: labelsNs })}
              </Typography>
            ) : null}
            {!hideTitle ? (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {t('avatar.hint', { ns: labelsNs })}
              </Typography>
            ) : null}
            {hideTitle ? (
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Box sx={{ width: 'max-content' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    fullWidth
                    startIcon={<PhotoCameraIcon />}
                    onClick={pickFile}
                    disabled={!canEdit}
                  >
                    {t(apiSrc ? 'avatar.change' : 'avatar.upload', { ns: labelsNs })}
                  </Button>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 1, whiteSpace: 'nowrap' }}
                  >
                    {t('avatar.hintShort', { ns: labelsNs })}
                  </Typography>
                </Box>
                {onRemove && apiSrc ? (
                  <Button
                    size="small"
                    color="error"
                    variant="text"
                    startIcon={<DeleteIcon />}
                    onClick={() => void handleRemove()}
                    disabled={!canEdit}
                  >
                    {t('avatar.remove', { ns: labelsNs })}
                  </Button>
                ) : null}
              </Stack>
            ) : (
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PhotoCameraIcon />}
                  onClick={pickFile}
                  disabled={!canEdit}
                >
                  {t(apiSrc ? 'avatar.change' : 'avatar.upload', { ns: labelsNs })}
                </Button>
                {onRemove && apiSrc ? (
                  <Button
                    size="small"
                    color="error"
                    variant="text"
                    startIcon={<DeleteIcon />}
                    onClick={() => void handleRemove()}
                    disabled={!canEdit}
                  >
                    {t('avatar.remove', { ns: labelsNs })}
                  </Button>
                ) : null}
              </Stack>
            )}
          </Box>
        </Stack>
      )}
      <Menu
        open={Boolean(menuAnchor)}
        anchorEl={menuAnchor}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={pickFile}>
          {t(apiSrc ? 'avatar.change' : 'avatar.upload', { ns: labelsNs })}
        </MenuItem>
        {apiSrc && onRemove ? (
          <MenuItem onClick={() => void handleRemove()} sx={{ color: 'error.main' }}>
            {t('avatar.remove', { ns: labelsNs })}
          </MenuItem>
        ) : null}
      </Menu>
      <AvatarCropDialog
        open={cropOpen}
        imageSrc={cropSrc}
        fileName={cropFileName}
        labels={cropLabels}
        busy={cropping}
        onClose={closeCrop}
        onConfirm={handleCropConfirm}
      />
    </>
  );
};

export default AvatarUploadControl;
